package ui.campaign.detailv2.editor.page

import androidx.compose.animation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import api.CampaignType
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.plus
import org.ssoft.common.tools.nowDate
import ssoft.frontend.generated.resources.*
import tools.formatFromRes
import tools.rememberString
import ui.campaign.detailv2.editor.CampaignEditorScope
import ui.campaign.detailv2.editor.NextButton
import ui.campaign.detailv2.editor.wrapper.CampaignWrapperE
import ui.widget.*
import kotlin.time.Clock

private val contentSize = 512.dp

@Composable
fun CampaignEditorScope.CampaignPage00General(nnvl: CampaignWrapperE) {
    val state = rememberLazyListState()
    Box(Modifier.fillMaxSize()) {
        LazyColumn(
            state = state,
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(vertical = 16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            item {
                Card(
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.surfaceContainer
                    )
                ) {

                    Column(modifier = Modifier.padding(8.dp).width(contentSize)) {
                        Title(nnvl)
                        Space(24.dp)
                        DateTime(nnvl)
                        Space(24.dp)
                        TypeSwitchers(nnvl)
                        Counters(nnvl)
                        Space(24.dp)
                        Volume(nnvl)
                    }
                }
            }
            item {
                NextButton()
            }
        }
    }
}


@Composable
private fun CampaignEditorScope.Title(nnvl: CampaignWrapperE) {
    TextField(
        value = nnvl.general.name.text,
        isError = nnvl.general.name.err,
        onValueChange = { nnvl.general.name.set(it) },
        modifier = Modifier.fillMaxWidth().clip(CardDefaults.shape),
        leadingIcon = { Icon(Svg.Format_text, "") },
        label = { Text(Res.string.layer_top_listing__new_name_hint.rememberString()) },
        shape = CardDefaults.shape
    )
}


@Composable
private fun CampaignEditorScope.DateTime(nnvl: CampaignWrapperE) {

    val stateDate = DialogState.remember()

    val enabledRange = remember {
        if (nnvl.isStateOfDraft || nnvl.general.dtStart.state.date > Clock.nowDate()) {
            Clock.nowDate() to Clock.nowDate().plus(DatePeriod(days = 364))
        } else {
            nnvl.general.dtStart.state.date to Clock.nowDate().plus(DatePeriod(days = 364))
        }
    }

    EditorCardWrapper(
        icon = Svg.Calendar,
        title = Res.string.layer_top_detail_editor__date_range_title,
        tooltip = Res.string.layer_top_detail_editor__date_range_tooltip
    ) {
        Button(onClick = { stateDate.show() }) {
            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                Spacer(Modifier.weight(1F))
                Icon(Svg.Triangle, null)
                Text(
                    text = nnvl.general.dtStart.state.date.formatFromRes(),
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleMedium
                )
                Spacer(Modifier.weight(1F))
                Icon(Svg.Flag, null)
                Text(
                    text = nnvl.general.dtEnd.state.date.formatFromRes(),
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleMedium
                )
                Spacer(Modifier.weight(1F))
            }
        }
    }

    DateRangePickerDialog(
        state = stateDate,
        start = nnvl.general.dtStart,
        end = nnvl.general.dtEnd,
        enabledRange = enabledRange
    ) {

    }
}

@Composable
private fun CampaignEditorScope.TypeSwitchers(nnvl: CampaignWrapperE) {
    if (nnvl.data.settings.enableCommercial) {
        EditorCardWrapper(
            icon = Svg.Handshake_outline,
            title = Res.string.layer_top_detail_editor__usage_mode_title,
            tooltip = Res.string.layer_top_detail_editor__usage_mode_tooltip
        ) {
            SwitchCard(
                field = nnvl.general.isCommercial,
                label = Res.string.layer_top_detail_editor__usage_mode_commercial
            )
            SwitchCard(
                field = nnvl.general.isNonCommercial,
                label = Res.string.layer_top_detail_editor__usage_mode_non_commercial
            )
        }
    }
}

@Composable
private fun CampaignEditorScope.Counters(nnvl: CampaignWrapperE) {
    AnimatedVisibility(
        visible = nnvl.data.settings.enableCommercial && nnvl.general.isCommercial.state,
        enter = fadeIn() + expandVertically(),
        exit = fadeOut() + shrinkVertically()
    ) {
        Column {
            Space(24.dp)
            when (CampaignType[nnvl.data.settings.type]) {
                CampaignType.IMPRESSION -> CounterImpression(nnvl)
                CampaignType.SLOT -> CounterSlot(nnvl)
                CampaignType.NONE -> Unit
            }
        }

    }
}

@Composable
private fun CampaignEditorScope.CounterImpression(nnvl: CampaignWrapperE) {
    EditorCardWrapper(
        icon = Svg.Triangle,
        title = Res.string.layer_top_detail_editor__impression_title,
        tooltip = Res.string.layer_top_detail_editor__impression_tooltip
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {

            ElevatedCard(
                modifier = Modifier.weight(1F),
            ) {
                Text(
                    text = Res.string.layer_top_detail_editor__impression_duration.rememberString(),
                    style = MaterialTheme.typography.labelSmall,
                    modifier = Modifier.padding(start = 12.dp, top = 4.dp, bottom = 4.dp)
                )
                TextField(
                    value = nnvl.general.impressionDuration.text,
                    isError = nnvl.general.impressionDuration.err,
                    suffix = {
                        Text(text = Res.string.layer_top_detail_editor__impression_unit_duration.rememberString())
                    },
                    onValueChange = { nnvl.general.impressionDuration.set(it) },
                    colors = TextFieldDefaults.colors(
                        unfocusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                        focusedContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                    )
                )
            }

            ElevatedCard(
                modifier = Modifier.weight(1F)
            ) {
                Text(
                    text = Res.string.layer_top_detail_editor__impression_count.rememberString(),
                    style = MaterialTheme.typography.labelSmall,
                    modifier = Modifier.padding(start = 12.dp, top = 4.dp, bottom = 4.dp)
                )
                TextField(
                    value = nnvl.general.impressionCount.text,
                    isError = nnvl.general.impressionCount.err,
                    suffix = {
                        Text(text = Res.string.layer_top_detail_editor__impression_unit_count.rememberString())
                    },
                    onValueChange = { nnvl.general.impressionCount.set(it) },
                    colors = TextFieldDefaults.colors(
                        unfocusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                        focusedContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                    )
                )
            }
            ElevatedCard(
                modifier = Modifier.weight(1F)
            ) {
                Text(
                    text = Res.string.layer_top_detail_editor__impression_space.rememberString(),
                    style = MaterialTheme.typography.labelSmall,
                    modifier = Modifier.padding(start = 12.dp, top = 4.dp, bottom = 4.dp)
                )
                TextField(
                    value = nnvl.general.impressionSpace.text,
                    isError = nnvl.general.impressionSpace.err,
                    suffix = {
                        Text(text = Res.string.layer_top_detail_editor__impression_unit_space.rememberString())
                    },
                    onValueChange = { nnvl.general.impressionSpace.set(it) },
                    colors = TextFieldDefaults.colors(
                        unfocusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                        focusedContainerColor = MaterialTheme.colorScheme.surfaceContainerLow,
                    )
                )
            }
        }
    }
}

@Composable
private fun CampaignEditorScope.CounterSlot(nnvl: CampaignWrapperE) {
    val selectedSlot = remember {
        mutableStateOf(
            nnvl.general.slotsValues
                .firstOrNull { it.value == nnvl.general.slotsCount.state }
                ?: nnvl.general.slotsValues.first()
        )
    }
    EditorCardWrapper(
        icon = Svg.Select_group,
        title = Res.string.layer_top_detail_editor__slot_title,
        tooltip = Res.string.layer_top_detail_editor__slot_tooltip
    ) {
        DropDownPicker(
            modifier = Modifier.fillMaxWidth(),
            selected = selectedSlot,
            values = nnvl.general.slotsValues,
            previewImage = null,
            itemWidth = 512.dp,
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            )
        ) { nnvl.general.slotsCount.set(it.value) }
    }
}

@Composable
private fun CampaignEditorScope.Volume(nnvl: CampaignWrapperE) {
    EditorCardVolumeSetup(
        field = nnvl.general.volume,
        tooltip = Res.string.layer_top_detail_editor__audio_setup_tooltip
    )
}